import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import driverApplicationService from '../../services/driverApplicationService';
import { formatDisplayDateTime } from '../../utils/dateInput';

const POLL_MS = 60 * 1000;

/**
 * Header bell for admins/managers: driver-application notifications only
 * (new submissions, resubmissions, approvals, hires by other managers).
 * Polls every minute and when the tab regains focus.
 */
const ApplicationNotificationsBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ unreadCount: 0, notifications: [] });
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setData(await driverApplicationService.notifications());
    } catch (error) {
      // The bell is secondary UI; keep the last known state on failure.
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => { if (panelRef.current && !panelRef.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openNotification = async (notification) => {
    setOpen(false);
    if (!notification.read) {
      driverApplicationService.markNotificationsRead([notification.id]).then(setData).catch(() => null);
    }
    if (notification.actionUrl) navigate(notification.actionUrl);
  };

  const markAll = async () => {
    try {
      setData(await driverApplicationService.markNotificationsRead());
    } catch (error) {
      // ignore
    }
  };

  const count = data.unreadCount || 0;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-slate-400 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-600"
        aria-label={count ? `Notifications, ${count} unread` : 'Notifications'}
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">Driver applications</p>
            {count > 0 && (
              <button type="button" onClick={markAll} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900">
                <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" /> Mark all read
              </button>
            )}
          </div>
          {data.notifications.length ? (
            <ul className="max-h-96 divide-y divide-gray-100 overflow-y-auto">
              {data.notifications.map((n) => (
                <li key={n.id}>
                  <button type="button" onClick={() => openNotification(n)} className={`flex w-full gap-3 px-4 py-3 text-left hover:bg-gray-50 ${n.read ? '' : 'bg-blue-50/60'}`}>
                    <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${n.read ? 'bg-transparent' : 'bg-blue-600'}`} aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900">{n.title}</span>
                      <span className="mt-0.5 block text-xs text-gray-600">{n.message}</span>
                      <span className="mt-1 block text-[11px] text-gray-400">{formatDisplayDateTime(n.createdAt)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-4 py-8 text-center text-sm text-gray-500">No notifications yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ApplicationNotificationsBell;
